/**
 * useSearchLeads — the owner dropdown's colleague options.
 *
 * Keyed on `status` ALONE, which is the whole reason this is a second query
 * rather than a block on the list response: the options and their counts do
 * not depend on the page, and they do not depend on `q`. Folding them into the
 * list would have recomputed and re-shipped the aggregate on every debounced
 * keystroke.
 *
 * Never blocks the page. The caller reads `data?.items ?? []`, so a pending or
 * failed request leaves the dropdown with `Mine` / `Everyone` — the two options
 * that need no server.
 */

import type { SearchLeadsResponse, SearchStatusFilter } from "@nu-assist/contracts";
import { useQuery } from "@tanstack/react-query";

import type { SearchesClient } from "../../../clients/searches.client";

export interface UseSearchLeadsArgs {
  client: SearchesClient;
  status: SearchStatusFilter;
}

export function useSearchLeads({ client, status }: UseSearchLeadsArgs) {
  return useQuery<SearchLeadsResponse>({
    queryKey: ["searches", "list", "leads", status],
    queryFn: () => client.listSearchLeads({ status }),
    // The mirror is refreshed by a cron worker, so the option set cannot change
    // in response to anything the user does on this page.
    staleTime: 5 * 60 * 1000,
  });
}
